"use client";

import { useCallback, useEffect, useMemo } from "react";
import { usePrenotaStore } from "@PN/inclusao";
import type { ParcelaPayload, DateISO } from "@PN/inclusao";
import { useParcelasQuery } from "./_tanstack";
import { useParcelasValidation } from "./useParcelasValidation";
import { fmt } from "visuais";

export function useParcelas() {
  // ✅ CORRETO - Seletores separados para evitar loop infinito
  const pagamentos = usePrenotaStore((state) => state.draft.pagamentos);
  const setPagamentos = usePrenotaStore(
    (state) => state.actions.draft.setPagamentos
  );

  // Hook de validação separado
  const { valorNF, canFetch, params } = useParcelasValidation();

  // Estado local: parcelas salvas na store (sempre prioritárias)
  const parcelas = pagamentos.parcelas;

  // Consulta as parcelas da API - habilitada quando pode buscar
  const {
    data: parcelasApi,
    isLoading,
    error,
    refetch,
  } = useParcelasQuery(params, canFetch);

  // Verifica se as parcelas estão salvas e consistentes
  const areParcelasSaved = useMemo(() => {
    if (parcelas.length === 0) return false;
    return parcelas.every((p) => !!p.vencimento && p.valor > 0);
  }, [parcelas]);

  // Edita vencimento de uma parcela
  const handleVencimentoChange = useCallback(
    (idx: number, date: Date) => {
      const isoDate = fmt.dates.ISO(date) as DateISO;
      const novasParcelas = parcelas.map((p, i) =>
        i === idx ? { ...p, vencimento: isoDate } : p
      );

      setPagamentos({ ...pagamentos, parcelas: novasParcelas });
    },
    [parcelas, pagamentos, setPagamentos]
  );

  // Edita valor de uma parcela
  const handleValorChange = useCallback(
    (idx: number, valor: number) => {
      const novasParcelas = parcelas.map((p, i) =>
        i === idx ? { ...p, valor } : p
      );

      setPagamentos({ ...pagamentos, parcelas: novasParcelas });
    },
    [parcelas, pagamentos, setPagamentos]
  );

  // Adiciona uma nova parcela
  const handleAddParcela = useCallback(() => {
    const nextItem = String(parcelas.length + 1).padStart(3, "0");
    const novasParcelas: ParcelaPayload[] = [
      ...parcelas,
      { item: nextItem, vencimento: "" as DateISO, valor: 0 },
    ];
    setPagamentos({ ...pagamentos, parcelas: novasParcelas });
  }, [parcelas, pagamentos, setPagamentos]);

  // Remove uma parcela pelo índice
  const handleRemoveParcela = useCallback(
    (idx: number) => {
      if (parcelas.length <= 1) return;
      const novasParcelas = parcelas
        .filter((_, i) => i !== idx)
        .map((p, i) => ({ ...p, item: String(i + 1).padStart(3, "0") }));
      setPagamentos({ ...pagamentos, parcelas: novasParcelas });
    },
    [parcelas, pagamentos, setPagamentos]
  );


  // ✅ Salva automaticamente as parcelas da API na store quando chegarem
  // MAS só se não houver parcelas válidas editadas pelo usuário
  useEffect(() => {
    if (
      parcelasApi &&
      Array.isArray(parcelasApi) &&
      parcelasApi.length > 0 &&
      canFetch
    ) {
      // Verifica se as parcelas já estão salvas na store para evitar loops
      const currentParcelas =
        usePrenotaStore.getState().draft.pagamentos.parcelas;

      // ✅ NOVO: Verifica se já existem parcelas válidas editadas pelo usuário
      // Se o usuário já editou manualmente (tem parcelas com vencimento e valor), não sobrescreve
      const temParcelasValidasEditadas = currentParcelas.length > 0 && 
        currentParcelas.some((p) => p.vencimento && p.valor > 0);

      if (temParcelasValidasEditadas) {
        console.log(
          "💰 [Parcelas] Parcelas já editadas manualmente - preservando edições do usuário"
        );
        return; // Não sobrescreve edições manuais
      }

      // Comparação mais robusta
      const parcelasSaoIguais =
        currentParcelas.length === parcelasApi.length &&
        currentParcelas.every(
          (p, i) =>
            p.item === parcelasApi[i].item &&
            p.vencimento === parcelasApi[i].vencimento &&
            Math.abs(p.valor - parcelasApi[i].valor) < 0.01
        );

      if (!parcelasSaoIguais) {
        console.log(
          "💰 [Parcelas] Salvando automaticamente na store:",
          parcelasApi
        );
        const currentState = usePrenotaStore.getState();
        setPagamentos({
          ...currentState.draft.pagamentos,
          parcelas: parcelasApi,
        });
      }
    }
  }, [parcelasApi, canFetch, setPagamentos]);

  // Função para forçar nova busca e substituir edições
  const handleForceRefetch = useCallback(async () => {
    if (!canFetch) {
      console.warn("💰 [Parcelas] Não pode buscar - campos obrigatórios faltando");
      return;
    }
    
    console.log("💰 [Parcelas] Busca manual iniciada - substituindo edições");
    
    try {
      const result = await refetch();
      
      // ✅ SEMPRE salva quando for busca manual (botão de atualizar)
      if (result.data && Array.isArray(result.data) && result.data.length > 0) {
        console.log("💰 [Parcelas] Salvando parcelas do refetch manual:", result.data);
        const currentState = usePrenotaStore.getState();
        setPagamentos({
          ...currentState.draft.pagamentos,
          parcelas: result.data,
        });
      }
    } catch (error) {
      console.error("💰 [Parcelas] Erro ao buscar parcelas:", error);
    }
  }, [refetch, canFetch, setPagamentos]);

  return {
    parcelas,
    isLoading,
    error,
    areParcelasSaved,
    handleVencimentoChange,
    handleValorChange,
    handleAddParcela,
    handleRemoveParcela,
    handleForceRefetch,
    refetch,
    valorNF,
    pagamentos,
    canFetch,
  };
}
